import PropTypes from 'prop-types';
import { useState } from 'react';
import { useQuery, gql } from '@apollo/client';
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';

import config from 'config';
import useCustomTheme from 'hooks/useCustomTheme';
import { CampaignContributeProvider } from 'context/CampaignContribute';
import NotFoundPage from 'pages/404';
import CampaignContributeSEO from 'components/campaign-contribute/CampaignContributeSeo';
import CampaignContributeElementsProvider from 'components/campaign-contribute/CampaignContributeElementsProvider';
import WorkflowLayout from './WorkflowLayout';

const CAMPAIGN_CONTRIBUTION_LAYOUT_FIELDS = gql`
  fragment CampaignContributionLayoutFields on Campaign {
    id
    accentColor
    campaignLogo
    ownerOrganization {
      id
      logo
      name
      paymentProcessorConfig
    }
    primaryColor
  }
`;

const FROM_CAMPAIGN = gql`
  ${CAMPAIGN_CONTRIBUTION_LAYOUT_FIELDS}
  query GetCampaignContributeLayout($id: String!) {
    findCampaigns(id: $id) {
      ...CampaignContributionLayoutFields
    }
  }
`;

const FROM_FUNDRAIASER = gql`
  ${CAMPAIGN_CONTRIBUTION_LAYOUT_FIELDS}
  query GetCampaignContributeLayout($id: String!) {
    findFundraisers(id: $id) {
      id
      campaign {
        ...CampaignContributionLayoutFields
      }
    }
  }
`;

const FROM_TEAM = gql`
  ${CAMPAIGN_CONTRIBUTION_LAYOUT_FIELDS}
  query GetCampaignContributeLayout($id: String!) {
    findTeams(id: $id) {
      id
      campaign {
        ...CampaignContributionLayoutFields
      }
    }
  }
`;

const CampaignContributeLayout = ({ id, idType, showBackButton, children, pageTitleDetail }) => {
  const [campaign, setCampaign] = useState(null);

  const { loading } = useQuery(
    {
      campaign: FROM_CAMPAIGN,
      fundraiser: FROM_FUNDRAIASER,
      team: FROM_TEAM,
    }[idType],
    {
      variables: { id },
      onCompleted: (data) => {
        let fetchedCampaign = data?.findCampaigns?.[0];
        if (idType === 'fundraiser') fetchedCampaign = data?.findFundraisers?.[0]?.campaign;
        if (idType === 'team') fetchedCampaign = data?.findTeams?.[0]?.campaign;

        // In some situations we switch from a team/fundraiser idType to campaign (after updating
        // the contribution context) which causes our query hook to reset. If we don't maintain a
        // reference to the campaign data via useState it will temporarily return to null - causing
        // the contribution context to unmount & reset.
        setCampaign(fetchedCampaign);
      },
    }
  );

  const logo = campaign?.campaignLogo ?? campaign?.ownerOrganization.logo;
  const { accountId, publicKey } = campaign?.ownerOrganization?.paymentProcessorConfig ?? {};

  const theme = useCustomTheme({
    primary: campaign?.primaryColor,
    secondary: campaign?.accentColor,
  });

  if (!campaign) return loading ? null : <NotFoundPage />;
  return (
    <>
      {theme.style}
      <GoogleReCaptchaProvider reCaptchaKey={config('/google/reCaptchaKey')}>
        <CampaignContributeProvider campaignId={campaign.id}>
          <CampaignContributeSEO pageTitleDetail={pageTitleDetail} />
          <WorkflowLayout
            backAction={showBackButton ? 'back' : null}
            logo={logo}
            logoFallbackText={campaign.ownerOrganization.name}
            showRecaptchaDisclaimer
          >
            <CampaignContributeElementsProvider accountId={accountId} publicKey={publicKey}>
              <div className="container">
                <div className="max-w-7xl mx-auto">{children}</div>
              </div>
            </CampaignContributeElementsProvider>
          </WorkflowLayout>
        </CampaignContributeProvider>
      </GoogleReCaptchaProvider>
    </>
  );
};

CampaignContributeLayout.propTypes = {
  id: PropTypes.string.isRequired,
  idType: PropTypes.oneOf(['campaign', 'fundraiser', 'team']),
  children: PropTypes.node.isRequired,
  showBackButton: PropTypes.bool,
  pageTitleDetail: PropTypes.string,
};

CampaignContributeLayout.defaultProps = {
  idType: 'campaign',
  showBackButton: true,
  pageTitleDetail: null,
};

export default CampaignContributeLayout;
